import { commercialReference } from '../quoteReference';
import React, { useState } from 'react';
import { QUOTE_VERSION_STATUS_LABELS } from '../quoteVersionStatus';

const tabs = [
  ['summary', 'Synthèse'],
  ['versions', 'Versions'],
  ['documents', 'Documents'],
  ['history', 'Historique'],
];

const dateTime = (value) => value
  ? new Intl.DateTimeFormat('fr-FR', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value))
  : '-';

const statusLabels = QUOTE_VERSION_STATUS_LABELS;

const eventLabels = {
  quote_created: 'Devis créé',
  version_created: 'Version créée',
  version_cloned: 'Version clonée',
  version_validated: 'Version validée',
  version_reopened: 'Version repassée en brouillon',
  version_issued: 'Version éditée',
  price_forced: 'Prix forcé',
  coefficient_forced: 'Coefficient forcé',
  expense_forced: 'Frais forcé',
  draft_saved: 'Brouillon enregistré',
  document_generated: 'Document officiel généré',
  document_preview_generated: 'Prévisualisation générée',
  document_downloaded: 'Document téléchargé',
  version_sent: 'Version envoyée au client',
  version_accepted: 'Version acceptée',
  version_refused: 'Version refusée',
  version_cancelled: 'Version annulée',
  archived: 'Devis archivé',
};

const AuditEvent = ({ event, formatMoney, displayNumber }) => {
  const context = event.contextJson || {};
  const oldValues = event.oldValuesJson || {};
  const newValues = event.newValuesJson || {};
  const actor = event.actorSnapshotJson?.professionalName || [event.actor?.firstName, event.actor?.lastName].filter(Boolean).join(' ')
    || event.actor?.email
    || (event.userId ? `Utilisateur #${event.userId}` : 'Système');
  const target = context.lineDescription || context.expenseLabel || context.fileName;
  const before = event.eventType === 'price_forced'
    ? newValues.calculatedUnitSellingPrice
    : event.eventType === 'coefficient_forced'
      ? oldValues.effectiveCoefficient
      : event.eventType === 'expense_forced'
        ? oldValues.effectiveAmount
        : null;
  const after = event.eventType === 'price_forced'
    ? newValues.forcedUnitSellingPrice
    : event.eventType === 'coefficient_forced'
      ? newValues.forcedCoefficient
      : event.eventType === 'expense_forced'
        ? newValues.effectiveAmount
        : null;
  return (
    <article className="quote-audit-entry">
      <time>{dateTime(event.eventAt || event.createdAt)}</time>
      <div>
        <strong>{eventLabels[event.eventType] || event.eventType}{target ? ` sur « ${target} »` : ''}</strong>
        {displayNumber && <span>{displayNumber}</span>}
        {before !== null && after !== null && <b>{event.eventType === 'coefficient_forced' ? `${before} → ${after}` : `${formatMoney(before)} → ${formatMoney(after)}`}</b>}
        <span>Par {actor}{event.actorSnapshotJson?.professionalFunction ? ` · ${event.actorSnapshotJson.professionalFunction}` : ''}</span>
        {event.reason && <p>Motif : {event.reason}</p>}
        {event.eventType === 'draft_saved' && Array.isArray(context.dpgfImports) && context.dpgfImports.map((item) => (
          <p key={item.quoteLineId}>Import DPGF — ligne source {item.source?.rowNumber ?? item.sourceCatalogReference}
            {item.sellingPriceMode === 'forced' && <> · PU retenu : {formatMoney(item.forcedUnitSellingPrice)}</>}
            {item.source?.retainedTotalHT != null && <> · Total HT retenu : {formatMoney(item.source.retainedTotalHT)}</>}
            {item.source?.unitPrice?.state === 'valid' && <> · PU source : {item.source.unitPrice.raw}</>}
          </p>
        ))}
      </div>
    </article>
  );
};

const QuoteFolderView = ({
  folder,
  versions = [],
  documents = [],
  history = [],
  formatMoney,
  onBack,
  onOpenVersion,
  onModifyCurrent,
}) => {
  const [activeTab, setActiveTab] = useState('summary');
  const current = versions.find((version) => Number(version.id) === Number(folder.currentVersionId))
    || folder.currentVersion;
  const customer = folder.Client?.name
    || folder.client?.name
    || current?.customerLegalName
    || 'Client non renseigné';
  const affair = folder.Affair?.code || folder.affair?.code || (folder.affairId ? `#${folder.affairId}` : '-');

  return (
    <section className="quote-folder">
      <header className="quote-folder__header">
        <div>
          <p className="quote-folder__number">{folder.quoteNumber}</p>
          <h1>{folder.title}</h1>
          <div className="quote-folder__identity">
            <span>Client : <strong>{customer}</strong></span>
            <span>Affaire : <strong>{affair}</strong></span>
          </div>
        </div>
        <div className="quote-folder__status">
          <span>Statut commercial</span>
          <strong>{folder.commercialStatus || 'draft'}</strong>
          <span>Version courante</span>
          <strong>{current?.versionNumber ? commercialReference(folder.quoteNumber, current.versionNumber) : '-'} - {statusLabels[current?.status] || current?.status || '-'}</strong>
        </div>
        <div className="quote-folder__actions">
          {current && (
            <button className="basic-button active" type="button" onClick={() => onOpenVersion(current.id)}>
              {current.status === 'draft' ? 'Continuer le devis' : 'Consulter la version'}
            </button>
          )}
          {current && current.status !== 'draft' && (
            <button className="basic-button" type="button" onClick={() => onModifyCurrent(current.id)}>
              Modifier (nouvelle version)
            </button>
          )}
          <button className="basic-button" type="button" onClick={onBack}>Retour à la liste</button>
        </div>
      </header>

      <nav className="quote-folder__tabs" aria-label="Navigation du dossier">
        {tabs.map(([key, label]) => (
          <button
            key={key}
            className={activeTab === key ? 'active' : ''}
            type="button"
            onClick={() => setActiveTab(key)}
          >
            {label}
            {key === 'versions' && <span>{versions.length}</span>}
            {key === 'documents' && <span>{documents.length}</span>}
          </button>
        ))}
      </nav>

      {activeTab === 'summary' && (
        <div className="quote-folder__summary">
          <article><span>Montant HT courant</span><strong>{formatMoney(current?.totalSellingPriceHt)}</strong></article>
          <article><span>Versions</span><strong>{versions.length}</strong></article>
          <article><span>Version acceptée</span><strong>{folder.acceptedVersion?.versionNumber ? commercialReference(folder.quoteNumber, folder.acceptedVersion.versionNumber) : 'Aucune'}</strong></article>
          <article><span>Dernière activité</span><strong>{dateTime(current?.updatedAt || folder.updatedAt)}</strong></article>
          <div className="quote-folder__timeline quote-folder__timeline--compact">
            <h2>Chronologie des versions</h2>
            {versions.map((version) => (
              <button key={version.id} type="button" onClick={() => onOpenVersion(version.id)}>
                <i className={`quote-timeline-dot quote-timeline-dot--${version.status}`} />
                <span>{commercialReference(folder.quoteNumber, version.versionNumber)}</span>
                <strong>{version.versionLabel || statusLabels[version.status]}</strong>
                <small>{dateTime(version.updatedAt)}</small>
              </button>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'versions' && (
        <div className="quote-folder__timeline">
          <h2>Toutes les versions</h2>
          {versions.map((version) => (
            <article key={version.id}>
              <i className={`quote-timeline-dot quote-timeline-dot--${version.status}`} />
              <div>
                <strong>{commercialReference(folder.quoteNumber, version.versionNumber)} - {version.versionLabel || statusLabels[version.status]}</strong>
                <span>{statusLabels[version.status] || version.status} · {dateTime(version.updatedAt)}</span>
              </div>
              <b>{formatMoney(version.totalSellingPriceHt)}</b>
              <button className="basic-button" type="button" onClick={() => onOpenVersion(version.id)}>Ouvrir</button>
            </article>
          ))}
        </div>
      )}

      {activeTab === 'documents' && (
        <div className="quote-folder__resource-list">
          <h2>Documents du dossier</h2>
          {documents.length === 0 && <p>Aucun document généré ou associé.</p>}
          {documents.map((document) => (
            <article key={document.id}>
              <div><strong>{document.fileName || document.documentType}</strong><span>{document.documentType}</span></div>
              <span>{document.version?.versionNumber ? commercialReference(folder.quoteNumber, document.version.versionNumber) : '-'}</span>
              <span>{dateTime(document.generatedAt || document.createdAt)}</span>
            </article>
          ))}
        </div>
      )}

      {activeTab === 'history' && (
        <div className="quote-folder__resource-list">
          <h2>Historique du dossier</h2>
          {history.length === 0 && <p>Aucun événement enregistré.</p>}
          {history.map((event) => {
            const version = versions.find(item => String(item.id) === String(event.quoteVersionId));
            return <AuditEvent key={event.id} event={event} formatMoney={formatMoney} displayNumber={version ? commercialReference(folder.quoteNumber, version.versionNumber) : null} />;
          })}
        </div>
      )}
    </section>
  );
};

export default QuoteFolderView;
